import { ITagProps } from '@/shared/types/tag';
import { Icon } from '../Icon';
import { Avatar } from '../Avatar';
import { TruncatedWithTooltip } from '../Tooltip';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import { useState, type ReactNode } from 'react';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

/**
 * Tag - Componente de etiqueta/tag versátil y configurable
 *
 * Componente de tag que soporta múltiples colores, estados (selected, disabled),
 * avatares, iconos de cierre e información, y diferentes estilos visuales.
 *
 * @example
 * ```tsx
 * // Tag simple
 * <Tag label="React" color="accent" />
 *
 * // Tag con avatar
 * <Tag
 *   label="Usuario"
 *   avatar="https://example.com/avatar.jpg"
 *   color="success"
 * />
 *
 * // Tag con botón de cerrar
 * <Tag
 *   label="Filtro"
 *   onClose={() => console.log('cerrado')}
 *   color="info"
 * />
 *
 * // Tag seleccionado
 * <Tag
 *   label="Seleccionado"
 *   selected
 *   onClick={() => console.log('clicked')}
 * />
 * ```
 *
 * @see {@link ITagProps} para la lista completa de props
 */
export const Tag = ({
  label = '',
  avatar,
  color = 'accent',
  onClose,
  className,
  stroke = true,
  onClick,
  disabled = false,
  selected = false,
  infoIcon = undefined,
  truncateLabel = false,
  truncateTooltip = false,
  ...props
}: ITagProps) => {
  const [isPressed, setIsPressed] = useState(false);

  // ============================================
  // ESTILOS BASE - Colores del Tag
  // ============================================
  const colorVariants = cva('', {
    variants: {
      color: {
        accent: 'ui-bg-surface-brand ui-text-brand ui-border-brand',
        gray: 'ui-bg-fill-tertiary ui-text-primary ui-border-default',
        success: 'ui-bg-surface-success ui-text-success ui-border-success',
        warning: 'ui-bg-surface-warning ui-text-warning ui-border-warning',
        danger: 'ui-bg-surface-danger ui-text-danger ui-border-danger',
        info: 'ui-bg-surface-info ui-text-info ui-border-info',
        disabled: 'ui-bg-surface-disabled ui-text-disabled ui-border-none ui-cursor-not-allowed',
      },
    },
    defaultVariants: {
      color: 'accent',
    },
  });

  // ============================================
  // FUNCIÓN HELPER PARA ESTILOS DE ESTADO (hover, pressed, selected)
  // ============================================
  // Esta función genera los estilos de estado que se reutilizan tanto para el tag como para la X mark
  const getStateStyles = (color: string, pressed: boolean, selected: boolean) => {
    const stateMap: Record<string, string> = {
      accent: cn(
        'hover:ui-bg-surface-brand-hover hover:ui-text-brand-hover',
        pressed && 'active:ui-bg-surface-brand-pressed active:ui-text-brand-pressed',
        selected && 'ui-bg-surface-brand-selected',
      ),
      gray: cn(
        'hover:ui-bg-fill-tertiary-hover',
        pressed && 'active:ui-bg-fill-tertiary-pressed',
        selected && 'ui-bg-fill-tertiary-selected',
      ),
      success: cn(
        'hover:ui-bg-surface-success-hover hover:ui-text-success-hover',
        pressed && 'active:ui-bg-surface-success-pressed active:ui-text-success-pressed',
        selected && 'ui-bg-surface-success-selected',
      ),
      warning: cn(
        'hover:ui-bg-surface-warning-hover hover:ui-text-warning-hover',
        pressed && 'active:ui-bg-surface-warning-pressed active:ui-text-warning-pressed',
        selected && 'ui-bg-surface-warning-selected',
      ),
      danger: cn(
        'hover:ui-bg-surface-danger-hover hover:ui-text-danger-hover',
        pressed && 'active:ui-bg-surface-danger-pressed active:ui-text-danger-pressed',
        selected && 'ui-bg-surface-danger-selected',
      ),
      info: cn(
        'hover:ui-bg-surface-info-hover hover:ui-text-info-hover',
        pressed && 'active:ui-bg-surface-info-pressed active:ui-text-info-pressed',
        selected && 'ui-bg-surface-info-selected',
      ),
      disabled: '',
    };
    return stateMap[color] || '';
  };

  // ============================================
  // ESTILOS DEL TAG CUANDO NO TIENE X MARK
  // ============================================
  const tagStatesWithoutClose = cva('', {
    variants: {
      color: {
        accent: getStateStyles('accent', isPressed, selected),
        gray: getStateStyles('gray', isPressed, selected),
        success: getStateStyles('success', isPressed, selected),
        warning: getStateStyles('warning', isPressed, selected),
        danger: getStateStyles('danger', isPressed, selected),
        info: getStateStyles('info', isPressed, selected),
        disabled: '',
      },
    },
  });

  // ============================================
  // ESTILOS DEL ICONO IZQUIERDO
  // ============================================
  const leftIconStyles = cva('', {
    variants: {
      color: {
        accent: cn(onClose && 'ui-text-brand'),
        gray: cn(onClose && 'ui-text-tertiary'),
        success: cn(onClose && 'ui-text-success'),
        warning: cn(onClose && 'ui-text-warning'),
        danger: cn(onClose && 'ui-text-danger'),
        info: cn(onClose && 'ui-text-info'),
        disabled: '',
      },
    },
  });

  const leftIconContainerStyles = cva('ui-shrink-0 ui-flex ui-items-center', {
    variants: {
      color: {
        accent: '',
        gray: '',
        success: '',
        warning: '',
        danger: '',
        info: '',
        disabled: '',
      },
    },
  });

  // ============================================
  // ESTILOS BASE DE LA X MARK
  // ============================================
  const closeButtonBaseStyles = cva(
    'ui-flex ui-items-center ui-justify-center ui-shrink-0 ui-rounded-control-xs ui-transition-colors ui-p-2',
    {
      variants: {
        color: {
          accent: '',
          gray: '',
          success: '',
          warning: '',
          danger: '',
          info: '',
          disabled: 'ui-opacity-50',
        },
      },
    },
  );

  // ============================================
  // ESTADOS DE LA X MARK (hover, pressed, selected)
  // ============================================
  // Reutilizamos los mismos estilos que tagStatesWithoutClose usando la función helper
  const closeButtonStates = cva('', {
    variants: {
      color: {
        accent: getStateStyles('accent', isPressed, selected),
        gray: getStateStyles('gray', isPressed, selected),
        success: getStateStyles('success', isPressed, selected),
        warning: getStateStyles('warning', isPressed, selected),
        danger: getStateStyles('danger', isPressed, selected),
        info: getStateStyles('info', isPressed, selected),
        disabled: '',
      },
    },
  });

  const isDisabled = disabled || color === 'disabled';
  // Cuando está disabled, forzar el color a 'disabled' para que todos tengan el mismo estilo gris
  const effectiveColor = isDisabled ? 'disabled' : color;

  const baseClasses = cn(
    'ui-inline-flex ui-items-center ui-font-body-xs-semibold ui-transition-colors',
    'ui-text-body-xs-regular ui-pl-6 ui-pr-4 ui-py-2 ui-gap-4 ui-rounded-control-sm',
    stroke ? 'ui-border' : 'ui-border-0',
    colorVariants({ color: effectiveColor }),
    // Si tiene onClose, NO aplicar estados al tag (solo a la X) y cursor normal
    // Si NO tiene onClose Y tiene onClick, aplicar estados al tag completo y cursor pointer
    // Si NO tiene onClose Y NO tiene onClick, cursor normal (informativo)
    !onClose && !isDisabled && onClick && 'ui-cursor-pointer',
    !onClose && !isDisabled && onClick && tagStatesWithoutClose({ color: effectiveColor }),
    !onClose && !isDisabled && !onClick && 'ui-cursor-default',
    onClose && !isDisabled && 'ui-cursor-default',
    onClose && 'ui-pr-1',
    truncateLabel && 'ui-min-w-0 ui-max-w-full ui-overflow-hidden',
    className,
  );

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!isDisabled) {
      setIsPressed(true);
      setTimeout(() => setIsPressed(false), 150);
      onClose?.(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClose && !isDisabled && onClick) {
      setIsPressed(true);
      setTimeout(() => setIsPressed(false), 150);
      onClick(e);
    }
  };

  const handleMouseDown = () => {
    if (!isDisabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  const closeButtonClasses = cn(
    closeButtonBaseStyles({ color: effectiveColor }),
    !isDisabled && 'ui-cursor-pointer',
    !isDisabled && closeButtonStates({ color: effectiveColor }),
    isDisabled && 'ui-cursor-not-allowed',
  );

  let infoIconEl: ReactNode = null;
  if (infoIcon) {
    const {
      name,
      size = 'xs',
      color: infoIconColor,
      className: infoIconClassName,
      ...infoIconRest
    } = infoIcon;
    infoIconEl = (
      <div className={leftIconContainerStyles({ color: effectiveColor })}>
        <Icon
          name={name}
          size={size}
          color={infoIconColor ?? 'current'}
          className={cn(
            infoIconColor === undefined && leftIconStyles({ color: effectiveColor }),
            infoIconClassName,
          )}
          {...infoIconRest}
        />
      </div>
    );
  }

  return (
    <div
      className={cn('ui-imj', baseClasses)}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      {...props}
    >
      {/* Left Icon or Avatar */}
      {avatar ? (
        <Avatar type="people" src={avatar} size="xxs" className="ui-shrink-0" />
      ) : (
        infoIconEl
      )}

      {/* Label */}
      {label && (
        <TruncatedWithTooltip
          truncate={truncateLabel}
          title={label}
          showTooltipWhenTruncated={truncateTooltip}
          className="ui-min-w-0 ui-text-body-xs-semibold ui-font-body-xs-semibold ui-leading-body-xs-semibold ui-tracking-body-xs"
        >
          {label}
        </TruncatedWithTooltip>
      )}

      {/* Close Icon */}
      {onClose && (
        <button
          type="button"
          onClick={handleClose}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className={closeButtonClasses}
          disabled={isDisabled}
          aria-label="Close"
        >
          <Icon
            name={faXmark}
            size="xs"
            color="current"
            className={leftIconStyles({ color: effectiveColor })}
          />
        </button>
      )}
    </div>
  );
};
